import { useEffect } from "react";

/**
 * SEO — imperatively sets <head> metadata for the current page.
 * Cleans up on unmount so SPA route changes don't accumulate stale tags.
 * This guarantees crawlers, WhatsApp/Twitter/Facebook scrapers, and LLMs
 * see only the current page's title / description / og:* / twitter:* / canonical.
 */

const SITE_NAME = "Conlang Book House";
const SITE_URL = "https://conlangbookhouse.com";
const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1437751068958-82e6fccc9360?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&h=630&fit=crop";
const DEFAULT_DESCRIPTION =
  "Conlang Book House (CBH) — a premium publisher of biographies, wisdom, self-help, women leadership, and geo-politics books from Bharat. Powered by Conlang International.";

const TAG_MARKER = "data-cbh-seo";

const upsertMeta = (attr, key, content) => {
  if (!content) return null;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(TAG_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertLink = (rel, href) => {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(TAG_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const setJsonLd = (jsonLd) => {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${TAG_MARKER}]`)
    .forEach((n) => n.remove());
  if (!jsonLd) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(TAG_MARKER, "true");
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
};

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_OG_IMAGE,
  imageAlt = "Conlang Book House — Where Ideas Become Institutions",
  type = "website",
  keywords,
  jsonLd,
  noindex = false,
  extraMeta = [],
}) => {
  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Where Ideas Become Institutions`;
  const url = `${SITE_URL}${path || ""}`;

  useEffect(() => {
    document.title = fullTitle;

    // Standard SEO
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );
    upsertMeta("name", "googlebot", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:locale", "en_IN");

    // Twitter / X
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", imageAlt);

    // Page-specific extra meta (e.g. book:isbn, book:release_date)
    extraMeta.forEach(({ attr, key, content }) => upsertMeta(attr, key, content));

    // JSON-LD structured data
    setJsonLd(jsonLd);

    return () => {
      // Route change / unmount: remove JSON-LD so next page can inject its own.
      document.head
        .querySelectorAll(`script[type="application/ld+json"][${TAG_MARKER}]`)
        .forEach((n) => n.remove());
    };
  }, [fullTitle, description, url, image, imageAlt, type, keywords, jsonLd, noindex, extraMeta]);

  return null;
};

export default SEO;
